if (!customElements.get("pcard-swatch")) {
  class ProductCardColorSwatch extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        container: "[data-pcard-variant-picker]",
        optionNodes: [".m-product-option--node__label"],
        featuredImage: ".m-product-card__main-image",
        pcard: ".m-product-card",
        variantDropdown: ".m-product-option--dropdown-select",
        priceWrapper: ".m-price",
        salePrice: ".m-price-item--sale",
        compareAtPrice: [".m-price-item--regular"],
        unitPrice: ".m-price__unit",
        soldOutBadge: ".m-product-tag--soldout",
        effectiveDiscountBadge: "[data-effective-discount-badge]",
        clearanceBadge: "[data-product-card-clearance]"
      };
      this.container = this.closest(this.selectors.container);
      this.pcard = this.closest(this.selectors.pcard);
      this.variantIdNode = this.pcard && this.pcard.querySelector('[name="id"]');
      this.featuredImage = this.pcard && this.pcard.querySelector(this.selectors.featuredImage);
      this.domNodes = queryDomNodes(this.selectors, this.pcard);
      this.initializeSwatchOverflow();
      this.initializeData();
    }

    get disableSelectedVariantByDefault() {
      return this.dataset.disableSelectedVariantByDefault === "true";
    }

    initializeSwatchOverflow() {
      this.swatchContent = this.querySelector(".m-product-option--swatch-overflow");
      this.moreSwatches = this.querySelector("[data-pcard-swatch-more]");
      if (!this.swatchContent || !this.moreSwatches) return;

      this.updateSwatchOverflow = this.updateSwatchOverflow.bind(this);
      this.swatchResizeObserver = new ResizeObserver(this.updateSwatchOverflow);
      this.swatchResizeObserver.observe(this.swatchContent);
      this.updateSwatchOverflow();
    }

    updateSwatchOverflow() {
      const options = Array.from(this.swatchContent.querySelectorAll(":scope > .m-product-option--node"));
      if (!options.length || !this.swatchContent.clientWidth) return;

      options.forEach((option) => (option.hidden = false));
      this.moreSwatches.hidden = false;

      const contentRight = this.swatchContent.getBoundingClientRect().right;
      const moreWidth = this.moreSwatches.getBoundingClientRect().width;
      const staticOverflow = Number(this.moreSwatches.dataset.staticOverflow || 0);
      const overflowingOptionIndex = options.findIndex((option) => option.getBoundingClientRect().right > contentRight);
      if (overflowingOptionIndex === -1 && staticOverflow === 0) {
        this.moreSwatches.hidden = true;
        return;
      }

      const visibleCount = options.findIndex((option) => option.getBoundingClientRect().right + moreWidth > contentRight);
      const hiddenCount = staticOverflow + options.length - (visibleCount === -1 ? options.length : visibleCount);

      options.slice(visibleCount === -1 ? options.length : visibleCount).forEach((option) => (option.hidden = true));
      this.moreSwatches.hidden = hiddenCount === 0;
      this.moreSwatches.querySelector("[data-pcard-swatch-more-count]").textContent = `+${hiddenCount}`;
      const moreLabel = this.moreSwatches.querySelector("[data-pcard-swatch-more-label]");
      moreLabel.dataset.template ||= moreLabel.textContent.trim();
      moreLabel.textContent = moreLabel.dataset.template.replace("__count__", hiddenCount);
    }

    disconnectedCallback() {
      this.swatchResizeObserver?.disconnect();
    }

    async initializeData() {
      this.variantData = this.fetchVariantData();
      this.productHandle = this.container.dataset.productHandle;
      this.productData = await this.fetchProductJson();
      this.activeOptionNodeByPosition = {};
      this.hideUnavailableProductOptions = MinimogSettings.hide_unavailable_product_options;

      const { variantIdNode, productData, productData: { variants } = {} } = this;

      if (productData) {
        let currentVariantId = variantIdNode && Number(variantIdNode.value);
        if (!currentVariantId) {
          currentVariantId =
            productData.selected_or_first_available_variant && productData.selected_or_first_available_variant.id;
        }
        const currentVariant = variants.find((v) => v.id === currentVariantId) || variants[0];
        this.productData.initialVariant = currentVariant;
        if (!this.productData.selected_variant && variantIdNode && variantIdNode.dataset.selectedVariant) {
          this.productData.selected_variant = variants.find(
            (v) => v.id === Number(variantIdNode.dataset.selectedVariant)
          );
        }

        if (!this.disableSelectedVariantByDefault) {
          this.updateOptionsByVariant(currentVariant);
          this.updateProductImage(currentVariant);
          window.MinimogEvents.subscribe("m:image-loaded", () => {
            this.updateProductImage(currentVariant);
          });
        }
      }

      if (this.domNodes.optionNodes) {
        this.domNodes.optionNodes.forEach((node) =>
          node.addEventListener("click", this.handleVariantSelection.bind(this))
        );
      }
      if (this.domNodes.variantDropdown) {
        this.domNodes.variantDropdown.addEventListener("change", this.handleVariantSelection.bind(this));
      }
    }

    fetchVariantData() {
      if (!this.variantData) {
        var jsonElement = this.container && this.container.querySelector('[type="application/json"]');
        this.variantData = JSON.parse(jsonElement ? jsonElement.textContent : "{}");
      }
      return this.variantData;
    }

    fetchProductJson() {
      var productUrl = `${window.MinimogSettings.routes.root}/products/${this.productHandle}.js`;
      productUrl = productUrl.replace("//", "/");
      return fetch(productUrl).then(function (response) {
        return response.json();
      });
    }

    toggleOptionNodeActive(optNode, active) {
      if (!optNode) return;
      if (active) {
        const { optionPosition, value: optionValue } = optNode.dataset;
        this.activeOptionNodeByPosition[optionPosition] = optNode;

        switch (optNode.tagName) {
          case "INPUT":
            optNode.checked = true;
            optNode.dataset.selected = "true";
            break;
          case "OPTION":
            optNode.dataset.selected = "true";
            const select = optNode.closest("select");
            if (select) select.value = optNode.value;
            break;
          case "LABEL":
            optNode.dataset.selected = "true";
            break;
          default:
            if (!optNode.classList.contains("m-product-quickview-button")) {
              console.warn("Unable to activate option node", optNode);
            }
            break;
        }

        this.updateSoldOutBadge(this.getVariantsByOptionValue(optionValue));
      } else {
        if (!["default", "image", "color"].includes(optNode.dataset.optionType)) {
          optNode.style.border = "";
        }
        optNode.checked = false;
        delete optNode.dataset.selected;
        const select = optNode.closest("select");
        if (select) select.value = "";
      }
    }

    updateOptionsByVariant(variant) {
      Object.values(this.activeOptionNodeByPosition).forEach((optNode) => this.toggleOptionNodeActive(optNode, false));

      const { optionNodes } = this.domNodes;
      const { options = [] } = variant || {};
      options.forEach((option, index) => {
        const optPosition = index + 1;
        optionNodes.forEach((optNode) => {
          const _optPosition = Number(optNode.dataset.optionPosition);
          const _optValue = optNode.dataset.value;

          if (_optPosition === optPosition && option === _optValue) {
            this.toggleOptionNodeActive(optNode, true);
          }
        });
      });
      this.updatePrice(variant);
    }

    getVariantFromActiveOptions = () => {
      const {
        productData,
        productData: { initialVariant },
        activeOptionNodeByPosition,
      } = this;
      const initialVariantOptions = {
        1: initialVariant.option1,
        2: initialVariant.option2,
        3: initialVariant.option3,
      };

      Object.values(activeOptionNodeByPosition).forEach((optNode) => {
        const { optionPosition, value } = optNode.dataset;
        initialVariantOptions[optionPosition] = value;
      });

      var options = Object.values(initialVariantOptions).filter(Boolean);
      var variant = getVariantFromOptionArray(productData, options);
      if (!variant && this.hideUnavailableProductOptions) {
        options.pop();
        variant = getVariantFromOptionArray(productData, options);
        if (!variant) {
          options.pop();
          variant = getVariantFromOptionArray(productData, options);
        }
      }
      this.currentVariant = variant;
      return variant;
    };

    handleVariantSelection(e) {
      var target = e.target;
      var newVariant;

      if (target.classList.contains("combined-variant")) {
        const variantId = Number(e.target.value);
        newVariant =
          this.productData && this.productData.variants && this.productData.variants.find((v) => v.id === variantId);
      } else {
        if (target.tagName === "SELECT") {
          target = target.querySelectorAll("option")[target.selectedIndex];
        }
        if (!target.classList.contains("m-product-option--node__label")) {
          target = target.closest(".m-product-option--node__label");
          if (!target) console.error("Unable to find option node!");
        }
        const { optionPosition } = target.dataset;

        const currActiveOptNode = this.activeOptionNodeByPosition[optionPosition];
        this.toggleOptionNodeActive(currActiveOptNode, false);
        this.toggleOptionNodeActive(target, true);
        newVariant = this.getVariantFromActiveOptions();
      }

      const { variantIdNode } = this;
      if (variantIdNode) {
        variantIdNode.setAttribute("value", String(newVariant.id));
        variantIdNode.value = String(newVariant.id);
      }

      this.updateBySelectedVariant(newVariant);
      this.updateEffectiveDiscountBadge(newVariant);
    }

    updateBySelectedVariant(variant) {
      if (variant) {
        this.updateProductImage(variant);
        this.updatePrice(variant);
      }
    }

    updateProductCardSoldOutBadge(variant) {
      if (this.domNodes.soldOutBadge) {
        this.domNodes.soldOutBadge.style.display = variant.available ? "none" : "flex";
      }
    }

    updateSoldOutBadge(variants) {
      const soldOut = !variants.some((variant) => variant.available);
      if (this.domNodes.soldOutBadge) {
        this.domNodes.soldOutBadge.style.display = soldOut ? "flex" : "none";
      }
    }

    updateEffectiveDiscountBadge(variant) {
      if (!variant || !this.domNodes.effectiveDiscountBadge) return;

      const discountPercent = this.domNodes.effectiveDiscountBadge.querySelector('[data-effective-discount-percent]');
      if (!discountPercent) return;

      const finalPriceRate = this.domNodes.priceWrapper && this.domNodes.priceWrapper.dataset.finalPriceRate;
      const finalPrice = window.NelpandaFinalPrice && window.NelpandaFinalPrice.projectVariant(variant, finalPriceRate, this.productHandle);
      const productPrice = finalPrice && finalPrice.active ? finalPrice.price : Number(variant.price || 0);
      const compareAtPrice = finalPrice && finalPrice.active
        ? finalPrice.referencePrice
        : Number(variant.compare_at_price || 0);
      const referencePrice = compareAtPrice > productPrice ? compareAtPrice : productPrice;
      const eligible = window.NelpandaFinalPrice?.variantDiscountEligible(variant, this.productHandle, this.domNodes.priceWrapper?.dataset.productDiscountEligible !== 'false') ?? (this.domNodes.priceWrapper?.dataset.productDiscountEligible !== 'false');
      const displayPrice = window.NelpandaFinalPrice?.applyOrderDiscount(productPrice, undefined, eligible) ?? productPrice;
      const percent = referencePrice > displayPrice
        ? Math.round((referencePrice - displayPrice) / referencePrice * 100)
        : 0;

      this.domNodes.effectiveDiscountBadge.dataset.productPrice = String(productPrice);
      this.domNodes.effectiveDiscountBadge.dataset.referencePrice = String(referencePrice);
      this.domNodes.effectiveDiscountBadge.dataset.productDiscountEligible = String(eligible);
      this.domNodes.effectiveDiscountBadge.hidden = percent <= 0;
      this.domNodes.effectiveDiscountBadge.classList.toggle('!m:hidden', percent <= 0);
      discountPercent.textContent = String(percent);
    }

    getVariantsByOptionValue = (value) => {
      const { productData: { variants } = {} } = this;
      return variants.filter((variant) => variant.options.includes(value));
    };

    updateProductImage(variant) {
      const src = variant && variant.featured_image && variant.featured_image.src;
      const { featuredImage } = this;
      const img = featuredImage && featuredImage.querySelector("img");

      if (img && src) {
        img.src = `${src}&width=533`;
        img.removeAttribute("srcset");
      }
    }

    updatePrice(variant) {
      const classes = {
        onSale: "m-price--on-sale",
        soldOut: "m-price--sold-out",
      };

      const moneyFormat = window.MinimogSettings.money_format;

      const { priceWrapper, salePrice, unitPrice, compareAtPrice } = this.domNodes;
      const { compare_at_price, price, unit_price_measurement } = variant;
      const finalPriceRate = priceWrapper && priceWrapper.dataset.finalPriceRate;
      const finalPrice = window.NelpandaFinalPrice && window.NelpandaFinalPrice.projectVariant(variant, finalPriceRate, this.productHandle);
      const productDisplayPrice = finalPrice && finalPrice.active ? finalPrice.price : price;
      const eligible = window.NelpandaFinalPrice?.variantDiscountEligible(variant, this.productHandle, priceWrapper?.dataset.productDiscountEligible !== 'false') ?? (priceWrapper?.dataset.productDiscountEligible !== 'false');
      const displayPrice = window.NelpandaFinalPrice?.applyOrderDiscount(productDisplayPrice, undefined, eligible) ?? productDisplayPrice;
      const displayCompareAtPrice = finalPrice && finalPrice.active ? finalPrice.referencePrice : compare_at_price;
      const onSale = displayCompareAtPrice && displayCompareAtPrice > displayPrice;
      const soldOut = !variant.available;

      if (this.domNodes.clearanceBadge) {
        this.domNodes.clearanceBadge.hidden = !window.NelpandaFinalPrice?.isClearanceVariant(variant, this.productHandle);
      }

      if (onSale) {
        priceWrapper && priceWrapper.classList.add(classes.onSale);
      } else {
        priceWrapper && priceWrapper.classList.remove(classes.onSale);
      }

      if (soldOut) {
        priceWrapper && priceWrapper.classList.add(classes.soldOut);
      } else {
        priceWrapper && priceWrapper.classList.remove(classes.soldOut);
      }

      priceWrapper && priceWrapper.classList.remove("visibility-hidden");
      if (priceWrapper) {
        priceWrapper.dataset.productDiscountEligible = String(eligible);
        const campaignLabel = priceWrapper.querySelector('[data-product-discount-label]');
        if (campaignLabel) campaignLabel.textContent = window.NelpandaFinalPrice?.isClearanceVariant(variant, this.productHandle) ? 'CLEARANCE' : priceWrapper.dataset.campaignLabel;
        priceWrapper.dataset.productPrice = String(productDisplayPrice);
        priceWrapper.dataset.referencePrice = String(displayCompareAtPrice || productDisplayPrice);
      }
      if (salePrice) salePrice.innerHTML = formatMoney(displayPrice, moneyFormat);

      if (compareAtPrice && compareAtPrice.length && displayCompareAtPrice > displayPrice) {
        compareAtPrice.forEach((item) => (item.innerHTML = formatMoney(displayCompareAtPrice, moneyFormat)));
      } else {
        compareAtPrice.forEach((item) => (item.innerHTML = formatMoney(displayPrice, moneyFormat)));
      }

      if (unit_price_measurement && unitPrice && this.currentVariant) {
        unitPrice.classList.remove("f-hidden");
        const unitPriceContent = `<span>${formatMoney(
          this.currentVariant.unit_price,
          moneyFormat
        )}</span>/<span data-unit-price-base-unit>${this.getBaseUnit()}</span>`;
        unitPrice.innerHTML = unitPriceContent;
      } else {
        unitPrice && unitPrice.classList.add("f-hidden");
      }
      window.NelpandaFinalPrice?.refresh(priceWrapper);
    }

    getBaseUnit = () => {
      return this.currentVariant.unit_price_measurement.reference_value === 1
        ? this.currentVariant.unit_price_measurement.reference_unit
        : this.currentVariant.unit_price_measurement.reference_value +
        this.currentVariant.unit_price_measurement.reference_unit;
    };
  }

  customElements.define("pcard-swatch", ProductCardColorSwatch);
}
